import { useState } from "react";
import { Navigate } from "react-router";
import { styled } from "@pigment-css/react";
import { Kpi } from "@acto/taskpane/core/domain";
import { isoToUtc, toIsoDate, todayUtc } from "@acto/web-common/dates";
import {
  DEFAULT_PERIOD_SELECTION,
  resolvePeriodSelection,
  type PeriodSelection,
} from "@acto/taskpane/field-sales-2/utils/periodSelection";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { PeriodComparisonScope } from "@acto/taskpane/field-sales-2/components/patterns/PeriodComparisonScope/PeriodComparisonScope";
import { ShellTopBar } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar";
import { AnalysisBottomNav } from "@acto/taskpane/field-sales-2/AnalysisBottomNav";
import { useCanAccessAnalysis } from "@acto/taskpane/field-sales-2/data-access/useCanAccessAnalysis";
import { ProductDrillProvider } from "./ProductDrillContext";
import { AnalysisDrillNav } from "./AnalysisDrillNav";
import { TenantAnalysis } from "./TenantAnalysis";

const Content = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  paddingBottom: "var(--bottom-nav-opaque-height, 0px)",
  overflowX: "clip",
});

export function AnalysisPage() {
  const canAccess = useCanAccessAnalysis();
  const [selection, setSelection] = useState<PeriodSelection>(DEFAULT_PERIOD_SELECTION);
  const [selectedKpi, setSelectedKpi] = useState<Kpi>(Kpi.REVENUE);
  // Keyed on the day so a long-lived session (docked taskpane) re-anchors rolling presets when the
  // day rolls over instead of freezing the window at its mount-day.
  const dayKey = toIsoDate(todayUtc());
  const dateRange = resolvePeriodSelection(selection, isoToUtc(dayKey));
  const isCustomRange = selection.kind === "custom";

  if (!canAccess) {
    return <Navigate to={ActoRoutes.HOME} replace />;
  }

  return (
    <ProductDrillProvider customerId={undefined}>
      <ShellTopBar leading={<AnalysisDrillNav />} />
      <Content>
        <PeriodComparisonScope selection={selection}>
          <TenantAnalysis
            dateRange={dateRange}
            isCustomRange={isCustomRange}
            selection={selection}
            selectedKpi={selectedKpi}
          />
        </PeriodComparisonScope>
      </Content>
      <AnalysisBottomNav
        selectedKpi={selectedKpi}
        onKpiChange={setSelectedKpi}
        period={{ selection, onSelectionChange: setSelection }}
      />
    </ProductDrillProvider>
  );
}
